import { forwardRef } from 'react'

import type { VariantProps } from 'tailwind-variants'

import { cn } from '~/utils/cn'
import { tv } from '~/utils/tv'

import { useHandlerProgress } from './hooks/use-handler-progress'

type ProgressIndicatorProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof progressIndicatorStyles> & {
    /**
     * Representa o progresso atual em porcentagem (0 a 100).
     */
    percentage?: number
    /**
     * Define o tempo estimado (em milissegundos) para o progresso atingir 100%.
     */
    duration?: number
    /**
     * Indica se o fundo do indicador deve ser transparente.
     */
    bgTransparent?: boolean
    /**
     * Callback acionado ao final do progresso, quando percentage atinge 100%.
     */
    onComplete?(): void
  }

export const progressIndicatorStyles = tv({
  base: 'flex w-full min-w-48 overflow-hidden rounded-pill p-none',
  variants: {
    variant: {
      brand: '*:bg-action-fill-brand-primary-enabled',
      neutral: '*:bg-text-neutral',
      'neutral-readonly': '*:bg-text-neutral-readonly',
      inverse: '*:bg-text-neutral-inverse',
      informative: '*:bg-text-informative-alternative',
      success: '*:bg-text-success-alternative',
      warning: '*:bg-text-warning-alternative',
      danger: '*:bg-text-danger-alternative',
    },
    size: {
      tiny: 'h-tiny',
      // @TODO - Exchange for a semantic token
      mini: 'h-3',
      micro: 'h-2',
      nano: 'h-1',
      pico: 'h-[0.125rem]',
    },
  },
  defaultVariants: {
    variant: 'brand',
    size: 'tiny',
  },
})

const ProgressIndicator = forwardRef<HTMLDivElement, ProgressIndicatorProps>(
  (
    {
      variant,
      size,
      percentage = 0,
      duration = 0,
      onComplete,
      bgTransparent,
      className,
      ...props
    },
    ref
  ) => {
    if (percentage > 0 && duration) {
      throw new Error(
        "As propriedades 'percentage' e 'duration' são mutuamente exclusivas. Por favor, forneça apenas uma delas."
      )
    }

    if (!props['aria-labelledby']) {
      console.warn(
        'A propriedade "aria-labelledby" é obrigatória para acessibilidade. Por favor, forneça um rótulo para o componente.'
      )
    }

    const {
      currentPercentage,
      PROGRESS_INDICATOR_MIN_VALUE,
      PROGRESS_INDICATOR_MAX_VALUE,
    } = useHandlerProgress(percentage, duration, onComplete)

    const classname = cn(
      progressIndicatorStyles({
      variant,
      size,
      className: bgTransparent
      ? 'bg-transparent'
      : 'bg-[--color-fill-neutral-fallback]',
    }), className)

    return (
      <div
        {...props}
        role="meter"
        ref={ref}
        className={classname}
        style={
          // @TODO - Exchange for a semantic token
          {
            '--color-fill-neutral-fallback': '#E6E6E6',
            '--progress-width': `${currentPercentage}%`,
          } as Record<string, string>
        }
        aria-valuenow={currentPercentage}
        aria-valuemin={PROGRESS_INDICATOR_MIN_VALUE}
        aria-valuemax={PROGRESS_INDICATOR_MAX_VALUE}
      >
        <span
          className={cn(
            'block h-full w-[--progress-width] rounded-tr-pill rounded-br-pill transition-transform ease-linear'
          )}
        />
      </div>
    )
  }
)

ProgressIndicator.displayName = 'ProgressIndicator'

export { ProgressIndicator }
export type { ProgressIndicatorProps }
